import { useQuery } from "@apollo/client";
import { Alert, Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import copy from "clipboard-copy";
import { useCallback, useState } from "react";

import { INTAKE_ADDRESS_QUERY } from "./inboundQuery";

/**
 * The intake address, inline in the sentence that says what it does.
 *
 * Bold copyable text rather than a chip. It has to stand out as the one value you
 * hand to a customer, but a chip sat too tall inside a line of body copy and read as
 * a separate element dropped into the sentence; monospace before that looked like a
 * code sample.
 */
function IntakeAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void copy(address).then(() => setCopied(true));
  }, [address]);

  return (
    <Tooltip
      title={copied ? "Copied" : "Click to copy"}
      onClose={() => setCopied(false)}
      placement="top"
    >
      <Box
        component="span"
        role="button"
        tabIndex={0}
        onClick={handleCopy}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCopy();
          }
        }}
        sx={{
          fontWeight: 700,
          color: "text.primary",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
          "&:focus-visible": { outline: 2, outlineColor: "primary.main", outlineOffset: 2 },
        }}
      >
        {address}
      </Box>
    </Tooltip>
  );
}

/**
 * How manifest email intake works, and the address it happens at.
 *
 * This is the only written explanation of the feature anywhere — there is no runbook
 * and no docs page — so it states the whole mechanism rather than labelling the grids
 * below it. Cross-tenant, so it can say the rules once instead of per customer.
 */
export function IntakeHeader() {
  const { data, loading } = useQuery(INTAKE_ADDRESS_QUERY, { fetchPolicy: "cache-first" });
  const address = data?.emailIntakeAddress;

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Manifest Email Intake</Typography>

      {loading && !data && <Skeleton width="60%" />}

      {!loading && !address && (
        <Alert severity="warning">
          The intake address for this environment could not be determined, which means it is
          misconfigured. Allowlisting a sender still works, but check with engineering before giving
          anyone an address.
        </Alert>
      )}

      {address && (
        <Typography variant="body2" color="text.secondary">
          Mail from an allowed sender to <IntakeAddress address={address} /> lands as an RCRA
          Manifest document in that sender&apos;s organization and kicks off Document AI extraction.
          The sender gets an automated reply listing which attachments were filed and which were
          refused, and why. Mail from a sender nobody allowlists is dropped and never answered —
          replying would confirm to anyone that the address is monitored.
        </Typography>
      )}
    </Stack>
  );
}
